import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INK = "#000000";
const BG = "#FFFFFF";
const MUTE = "#666666";
const DISPLAY = "'Archivo Black', 'Inter', system-ui, sans-serif";
const BODY = "'Inter', system-ui, -apple-system, sans-serif";

const inputStyle = {
  width: "100%",
  border: `2px solid ${INK}`,
  background: BG,
  color: INK,
  padding: "13px 15px",
  fontFamily: BODY,
  fontSize: 15,
  fontWeight: 600,
  outline: "none",
  borderRadius: 0,
  boxSizing: "border-box",
};
const labelStyle = {
  display: "block",
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  marginBottom: 8,
  color: INK,
};

export default function ExpertApplicationView() {
  useEffect(() => {
    document.title = "Apply to the Bench — HEY SOVA";
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "",
    bio: "",
    linkedin_url: "",
    cal_link: "",
    expertise: "",
    hourly_rate_usd: "",
    session_price_usd: "",
    availability_note: "",
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {kind, text}

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setStatus(null);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role.trim(),
        bio: form.bio.trim(),
        linkedin_url: form.linkedin_url.trim(),
        cal_link: form.cal_link.trim(),
        expertise: form.expertise
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        hourly_rate_usd: form.hourly_rate_usd
          ? Math.max(0, parseInt(form.hourly_rate_usd, 10) || 0)
          : null,
        session_price_usd: form.session_price_usd
          ? Math.max(0, parseInt(form.session_price_usd, 10) || 0)
          : null,
        availability_note: form.availability_note.trim(),
      };
      const { data } = await axios.post(`${API}/bench/apply`, payload);
      const scored = data.ai_score && data.ai_score !== "unscored"
        ? ` AI screened: ${data.ai_score}.`
        : "";
      setStatus({
        kind: "ok",
        text:
          `Application received.${scored} Teresa reviews every one personally — expect a reply within a few business days.`,
      });
      setForm({
        name: "",
        email: "",
        role: "",
        bio: "",
        linkedin_url: "",
        cal_link: "",
        expertise: "",
        hourly_rate_usd: "",
        session_price_usd: "",
        availability_note: "",
      });
    } catch (err) {
      setStatus({
        kind: "err",
        text:
          err?.response?.data?.detail ||
          "Couldn't send — try again or email info@heysova.io directly.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="apply-to-bench-view"
      style={{ minHeight: "100vh", background: BG, color: INK, fontFamily: BODY }}
    >
      <header
        style={{
          padding: "22px 28px",
          borderBottom: `2px solid ${INK}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <a href="/" style={{ color: INK, textDecoration: "none", fontFamily: DISPLAY, fontSize: 22, letterSpacing: "-0.02em" }}>
          HEY SOVA
        </a>
        <a
          href="/"
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            textDecoration: "underline",
            textUnderlineOffset: 3,
            color: INK,
          }}
        >
          ← Back
        </a>
      </header>

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "56px 28px 80px" }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 800,
            marginBottom: 14,
          }}
        >
          Apply to the bench
        </div>
        <h1
          style={{
            fontFamily: DISPLAY,
            fontSize: "clamp(40px, 6.5vw, 68px)",
            lineHeight: 0.92,
            letterSpacing: "-0.03em",
            textTransform: "uppercase",
            margin: "0 0 22px",
            fontWeight: 400,
          }}
        >
          Be the human <span style={{ background: INK, color: BG, padding: "0 0.1em" }}>founders</span> book.
        </h1>
        <p style={{ fontSize: 17, fontWeight: 600, color: INK, lineHeight: 1.55, marginBottom: 40, maxWidth: 580 }}>
          The Executive Bench is a small group of executives founders book by the hour for the
          decisions an AI executive can't fully take. We're approving 25 to start. Tell us about
          yourself.
        </p>

        <form onSubmit={submit} data-testid="bench-apply-form" style={{ display: "grid", gap: 22 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label htmlFor="b-name" style={labelStyle}>Name *</label>
              <input id="b-name" data-testid="bench-name" value={form.name} onChange={set("name")} required style={inputStyle} maxLength={200} />
            </div>
            <div>
              <label htmlFor="b-email" style={labelStyle}>Email *</label>
              <input id="b-email" data-testid="bench-email" type="email" value={form.email} onChange={set("email")} required style={inputStyle} maxLength={200} />
            </div>
          </div>
          <div>
            <label htmlFor="b-role" style={labelStyle}>Headline role *</label>
            <input
              id="b-role"
              data-testid="bench-role"
              placeholder="Fractional CFO · Ex-Stripe · 3-time exit"
              value={form.role}
              onChange={set("role")}
              required
              style={inputStyle}
              maxLength={200}
            />
          </div>
          <div>
            <label htmlFor="b-bio" style={labelStyle}>Bio *</label>
            <textarea
              id="b-bio"
              data-testid="bench-bio"
              placeholder="Two to four sentences. Where you've operated, what you're known for, what you actually want to weigh in on."
              value={form.bio}
              onChange={set("bio")}
              required
              rows={6}
              style={{ ...inputStyle, lineHeight: 1.5, resize: "vertical", minHeight: 140 }}
              maxLength={3000}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label htmlFor="b-exp" style={labelStyle}>Expertise tags (comma-separated)</label>
              <input
                id="b-exp"
                data-testid="bench-expertise"
                placeholder="fundraising, growth, b2b sales"
                value={form.expertise}
                onChange={set("expertise")}
                style={inputStyle}
                maxLength={400}
              />
            </div>
            <div>
              <label htmlFor="b-rate" style={labelStyle}>Hourly rate (USD)</label>
              <input
                id="b-rate"
                data-testid="bench-rate"
                type="number"
                min="0"
                max="5000"
                placeholder="450"
                value={form.hourly_rate_usd}
                onChange={set("hourly_rate_usd")}
                style={inputStyle}
              />
            </div>
          </div>
          <div>
            <label htmlFor="b-session" style={labelStyle}>Session price (USD) *</label>
            <input
              id="b-session"
              data-testid="bench-session-price"
              type="number"
              min="0"
              max="5000"
              placeholder="250"
              value={form.session_price_usd}
              onChange={set("session_price_usd")}
              required
              style={inputStyle}
            />
            <div style={{ fontSize: 11, fontWeight: 600, color: MUTE, marginTop: 6, lineHeight: 1.5 }}>
              The flat amount founders are charged to book one session with you (typically 30–60 min).
              Hey Sova keeps 15%; you receive 85% after the session.
            </div>
          </div>
          <div>
            <label htmlFor="b-li" style={labelStyle}>LinkedIn URL *</label>
            <input
              id="b-li"
              data-testid="bench-linkedin"
              placeholder="https://linkedin.com/in/yourname"
              value={form.linkedin_url}
              onChange={set("linkedin_url")}
              required
              style={inputStyle}
              maxLength={300}
            />
            <div style={{ fontSize: 11, fontWeight: 600, color: MUTE, marginTop: 6, lineHeight: 1.5 }}>
              We'll auto-screen your bio with AI against your LinkedIn URL. Final approval is manual.
            </div>
          </div>
          <div>
            <label htmlFor="b-cal" style={labelStyle}>Cal.com booking link (optional)</label>
            <input
              id="b-cal"
              data-testid="bench-cal"
              placeholder="https://cal.com/yourname/30min"
              value={form.cal_link}
              onChange={set("cal_link")}
              style={inputStyle}
              maxLength={300}
            />
            <div style={{ fontSize: 11, fontWeight: 600, color: MUTE, marginTop: 6, lineHeight: 1.5 }}>
              If you have a Cal.com link, founders can book you directly from your profile.
              No Cal.com yet? Skip — they'll request via email and we'll relay.
            </div>
          </div>
          <div>
            <label htmlFor="b-avail" style={labelStyle}>Sample availability</label>
            <input
              id="b-avail"
              data-testid="bench-availability"
              placeholder="Tuesdays + Thursdays, 2–5pm ET"
              value={form.availability_note}
              onChange={set("availability_note")}
              style={inputStyle}
              maxLength={500}
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="bench-submit"
            style={{
              border: `2px solid ${INK}`,
              background: busy ? "#CCCCCC" : INK,
              color: BG,
              padding: "18px 22px",
              fontFamily: BODY,
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: busy ? "default" : "pointer",
              transition: "transform .15s ease, box-shadow .15s ease",
            }}
            onMouseEnter={(e) => {
              if (busy) return;
              e.currentTarget.style.transform = "translate(-3px,-3px)";
              e.currentTarget.style.boxShadow = `6px 6px 0 ${INK}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {busy ? "Sending…" : "Submit application →"}
          </button>

          {status && (
            <div
              data-testid="bench-status"
              style={{
                padding: "16px 18px",
                border: `2px solid ${INK}`,
                fontSize: 14,
                fontWeight: 600,
                background: status.kind === "ok" ? "#F0FFF4" : "#FFF0F0",
              }}
            >
              {status.text}
            </div>
          )}
        </form>

        <p style={{ marginTop: 36, fontSize: 12, color: MUTE, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Approved executives show up on the public Executive Bench. Hey Sova takes a 15% fee on
          each booking. Payouts paid out via Stripe Connect once bookings go live.
        </p>
      </main>
    </div>
  );
}
